import React, { useMemo } from 'react'
import { BoxProps } from 'rebass'

import { useMyUpcomingPaymentQuery } from '../../types/graphql'
import SFlex from 'components/opinionless/SFlex'
import SIcon from 'components/opinionless/SIcon'

import SText from 'components/opinionless/SText'
import { useTranslation, Trans } from '../../i18n'
import SLoadingIndicator from 'components/opinionless/SLoadingIndicator'
import HistorySvg from '@svgs/history.svg'
import { formatMoney } from '../../utils/money'
import moment from 'moment-timezone'
import { useSTheme } from 'styles/theme'
import ConditionalRender from '../../utils/conditionalRender'

type Props = BoxProps

const HomeUpcomingPayment: React.FC<Props> = (props: Props) => {
    const { ...otherProps } = props
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const { data, loading } = useMyUpcomingPaymentQuery()

    const upcomingPaymentAmountStr = useMemo(() => {
        if (data && data.myUpcomingPayment) {
            return formatMoney(data.myUpcomingPayment.amount)
        } else {
            return null
        }
    }, [data])

    const upcomingPaymentDateStr = useMemo(() => {
        if (data && data.myUpcomingPayment) {
            const scheduledDate = moment(data.myUpcomingPayment.scheduledDate).tz(window.ORGANIZATION_CONFIGS.timezone)
            if (scheduledDate.isSame(new Date(), 'day')) {
                return `${scheduledDate.format('MMM D, YYYY')}(${t('today')})`
            }
            if (scheduledDate.isSame(moment().add(1, 'day'), 'day')) {
                return `${scheduledDate.format('MMM D, YYYY')}(${t('tomorrow')})`
            }
            return scheduledDate.format('MMMM D, YYYY')
        } else {
            return null
        }
    }, [data, t])

    return (
        <SFlex
            sx={{
                flex: ['0 0 auto', '1 1 0', '1 1 0'],
                borderRadius: [0, 2, 2],
                boxShadow: 'main',
                bg: 'white',
                flexDirection: 'column',
                p: [0, 8, 8],
            }}
            {...otherProps}
        >
            <SFlex
                flexDirection="row"
                alignItems="center"
                sx={{
                    px: [6, 0, 0],
                    py: [5, 0, 0],
                    mb: [0, 6, 6],
                    borderBottomStyle: 'solid',
                    borderBottomWidth: [0.2, 0, 0],
                    borderBottomColor: 'gray150',
                }}
            >
                <SIcon display={['none', 'block', 'block']} icon={HistorySvg} size={20} tintColor="black" mr={4} />
                <SText
                    sx={{
                        fontWeight: 'bold',
                        fontSize: 4,
                        lineHeight: 1.4,
                        color: 'black',
                        mt: [2, 0, 0],
                        fontFamily: theme.fonts.default,
                    }}
                >
                    {t('home_upcoming')}
                </SText>
            </SFlex>
            <SFlex
                sx={{
                    px: [6, 0, 0],
                    py: [6, 0, 0],
                }}
            >
                {loading ? (
                    <SLoadingIndicator />
                ) : (
                    <React.Fragment>
                        <ConditionalRender if={upcomingPaymentAmountStr}>
                            <SText fontSize={4} color="gray400" lineHeight={1.4}>
                                <Trans i18nKey="common:home_upcoming_payment">
                                    You will receive your next payment of{' '}
                                    <SText
                                        as="span"
                                        sx={{
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {{ upcomingPaymentAmountStr }}
                                    </SText>{' '}
                                    <SText
                                        as="span"
                                        sx={{
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {{ upcomingPaymentDateStr }}
                                    </SText>
                                    .
                                </Trans>
                            </SText>
                        </ConditionalRender>
                        <ConditionalRender if={!upcomingPaymentAmountStr}>
                            <Trans i18nKey="common:no_upcoming_payments" />
                        </ConditionalRender>
                    </React.Fragment>
                )}
            </SFlex>
        </SFlex>
    )
}

export default HomeUpcomingPayment
